import { ingestUrl } from "@m8t-stack/telemetry-contract";
import { LocalCliError } from "./errors.js";
import { runAz } from "./az.js";

/** Mint an Azure identity token for the enroll proof. Returns null if unavailable. */
async function defaultToken(): Promise<string | null> {
  const out = await runAz([
    "account", "get-access-token",
    "--resource", "https://management.azure.com/",
    "--query", "accessToken", "-o", "tsv",
  ]);
  const token = out.trim();
  return token.length > 0 ? token : null;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Fallback enrollment path for pre-existing installs (ones that predate the
 * installer's own enroll-on-provision step). Mints an instance id + ingest
 * key from the m8t telemetry ingest, for the caller to persist to the
 * platform Key Vault as `m8t-ingest-key`.
 *
 * Pseudonymous by default: the body carries the schema version and nothing
 * that identifies the customer. `company` / `contactEmail` are OPT-IN and are
 * omitted entirely when absent or empty — never sent as empty strings. There
 * is deliberately no `subscriptionId` parameter.
 */
export async function enroll(args: {
  company?: string;
  contactEmail?: string;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  tokenImpl?: () => Promise<string | null>;
  retryDelayMs?: number;
}): Promise<{ instanceId: string; ingestKey: string }> {
  const f = args.fetchImpl ?? fetch;
  const url = args.baseUrl ? `${args.baseUrl.replace(/\/+$/, "")}/api/ingest/enroll` : ingestUrl("/api/ingest/enroll");
  const retryDelayMs = args.retryDelayMs ?? 1000;

  // A missing token must never block enrollment: the ingest decides whether a
  // proof is required, and this path stays usable when it is not.
  const token = await (args.tokenImpl ?? defaultToken)().catch(() => null);

  // Trim before the emptiness check: a whitespace-only value ("   ") is not a
  // value anyone typed on purpose, and sending it untrimmed to the
  // email-formatted field fails the ingest's validation with a 400 — which the
  // installer's own client classifies as final, so the install would finish
  // reporting success while silently un-enrolled. installer/lib.sh applies the
  // identical trim-then-omit rule; the two are independent implementations of
  // one wire contract and must agree branch-for-branch.
  const payload: Record<string, unknown> = { schemaVersion: 1 };
  const company = args.company?.trim();
  if (company) payload.company = company;
  const contactEmail = args.contactEmail?.trim();
  if (contactEmail) payload.contactEmail = contactEmail;
  payload.proof = token ? { type: "entra-jwt", token } : null;
  const body = JSON.stringify(payload);

  let res: Response | undefined;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      res = await f(url, { method: "POST", headers: { "content-type": "application/json" }, body });
    } catch (e) {
      throw new LocalCliError({ code: "TELEMETRY_ENROLL_UNREACHABLE", message: `Could not reach the telemetry ingest to enroll: ${e instanceof Error ? e.message : String(e)}` });
    }
    // 503 means the ingest could not verify the token right now — transient.
    // Every other status, including a 4xx rejection, is final.
    if (res.status !== 503 || attempt === 3) break;
    await sleep(retryDelayMs * attempt);
  }

  if (!res?.ok) {
    // The body is the ingest's `{error:code}` envelope — safe to surface. The
    // token is never interpolated into an error message.
    const detail = res ? await res.text().catch(() => "") : "";
    throw new LocalCliError({ code: "TELEMETRY_ENROLL_FAILED", message: `Enroll failed (HTTP ${res ? res.status.toString() : "?"}): ${detail}` });
  }
  const json = (await res.json()) as { instanceId?: string; ingestKey?: string };
  if (!json.instanceId || !json.ingestKey) {
    throw new LocalCliError({ code: "TELEMETRY_ENROLL_BAD_RESPONSE", message: "Enroll response missing instanceId/ingestKey." });
  }
  return { instanceId: json.instanceId, ingestKey: json.ingestKey };
}
